import { countModules } from "@/components/pv/RoofFit";

export const uid = () => Math.random().toString(36).slice(2, 10);
const DEFAULT_DIMS = { long: 1.72, short: 1.13 };
const CLOSED = ["signed", "superseded", "cancelled"];

// "1.72x1.13m" | "1722 × 1134 mm" → { long, short } in Metern
export const parseModuleDims = (size) => {
  const m = String(size || "").replace(/,/g, ".").match(/(\d+(?:\.\d+)?)\s*[x×*]\s*(\d+(?:\.\d+)?)/i);
  if (!m) return DEFAULT_DIMS;
  let a = parseFloat(m[1]);
  let b = parseFloat(m[2]);
  if (a > 100) { a /= 1000; b /= 1000; }
  return a > 0 && b > 0 ? { long: Math.max(a, b), short: Math.min(a, b) } : DEFAULT_DIMS;
};

export const baseModule = (img, mPerPx, dims) => {
  const long = mPerPx ? dims.long / mPerPx : Math.min(img.w, img.h) * 0.08;
  return { long, short: long * (dims.short / dims.long) };
};

export const panelWH = (p, mod) => (p.landscape ? [mod.long, mod.short] : [mod.short, mod.long]);

export const centroid = (panels, fallback) => (panels.length
  ? { x: panels.reduce((s, p) => s + p.x, 0) / panels.length, y: panels.reduce((s, p) => s + p.y, 0) / panels.length }
  : fallback);

export const rotateAround = (panels, c, deg) => {
  const a = (deg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  return panels.map((p) => {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    return { ...p, x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos, angle: (p.angle || 0) + deg };
  });
};

export const scaleAround = (panels, c, f) => panels.map((p) => ({ ...p, x: c.x + (p.x - c.x) * f, y: c.y + (p.y - c.y) * f }));

// Rechteckiges Raster (eher breit als hoch, wie ein typisches Dach), letzte Reihe zentriert.
export const gridPlace = (n, mod, center, angle = 0, landscape = false) => {
  const [w, h] = landscape ? [mod.long, mod.short] : [mod.short, mod.long];
  const gap = mod.long * 0.03;
  const cols = Math.max(1, Math.min(n, Math.ceil(Math.sqrt((n * 1.6 * h) / w))));
  const rows = Math.ceil(n / cols);
  const flat = Array.from({ length: n }, (_, i) => {
    const r = Math.floor(i / cols);
    const inRow = r === rows - 1 ? n - r * cols : cols;
    return { id: uid(), x: center.x + ((i % cols) - (inRow - 1) / 2) * (w + gap),
      y: center.y + (r - (rows - 1) / 2) * (h + gap), angle: 0, landscape };
  });
  return angle ? rotateAround(flat, center, angle) : flat;
};

export const offerModules = (o) => (o.items || [])
  .reduce((n, it) => n + (it.type === "module" && !it.is_free ? Number(it.quantity) || 0 : 0), 0);

export const buildRefs = (configs, offers, catalog) => {
  const byId = Object.fromEntries((catalog || []).map((c) => [c.id, c]));
  const modOf = (items) => byId[(items || []).find((it) => it.type === "module" || byId[it.component_id]?.type === "module")?.component_id] || null;
  const offs = (offers || []).filter((o) => !CLOSED.includes(o.status)).map((o) => ({
    key: `offer:${o.id}`, kind: "offer", id: o.id, modules: offerModules(o), comp: modOf(o.items),
    label: `Angebot ${o.offer_number}`,
  }));
  const cfgs = (configs || []).map((c) => ({
    key: `config:${c.id}`, kind: "config", id: c.id, modules: countModules(c.items, catalog), comp: modOf(c.items),
    label: `Konfiguration „${c.name}“`,
  }));
  return [...offs, ...cfgs];
};

export const fmtKwp = (v) => Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
